// CustomerExportMenu — same ExportMenu pattern as Reports, scoped to
// the customers list. Passes through the current filter state so the
// exported file matches what the user sees in the table.

import { useState } from 'react';
import { ChevronDown, FileText, FileSpreadsheet, FileType2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { isChunkLoadError } from '@/lib/lazyChunk';
import { downloadBlob } from '@/lib/api/invoice';
import {
  buildCustomerCsv,
  buildCustomerXlsx,
  buildCustomerPdf,
  type CustomerExportFilters,
  type CustomerExportCompanyInfo,
} from '@/lib/api/customersExport';
import { useCompanyInfo } from '@/hooks/useSettings';
import { cn } from '@/lib/format';
import type { Customer } from '@/types/domain';

export type CustomerExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface CustomerExportMenuProps {
  customers: Customer[];
  filters: CustomerExportFilters;
  disabled?: boolean;
}

interface FormatOption {
  format: CustomerExportFormat;
  label: string;
  description: string;
  icon: typeof FileText;
  build: () => Promise<Blob>;
  mime: string;
}

export function CustomerExportMenu({ customers, filters, disabled }: CustomerExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [busyFormat, setBusyFormat] = useState<CustomerExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A chunk the deploy renamed: only a reload can fetch the new one.
  const [staleBuild, setStaleBuild] = useState(false);

  const { data: companyDoc } = useCompanyInfo();
  const company: CustomerExportCompanyInfo = {
    name: companyDoc?.name || 'OPHTH POS',
    address: companyDoc?.address,
    phone: companyDoc?.phone,
    email: companyDoc?.email,
  };

  const ts = new Date().toISOString().slice(0, 10);
  const basename = `ophth-customers-${ts}`;

  // Count after filter — drives the per-option description so the user
  // knows what they're about to download.
  const filteredCount = customers.filter((c) => {
    if (filters.status === 'active' && !c.isActive) return false;
    if (filters.status === 'inactive' && c.isActive) return false;
    if (filters.search) {
      const term = filters.search.toLowerCase();
      const hay = `${c.name} ${c.company ?? ''} ${c.phone} ${c.email ?? ''}`.toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  }).length;

  const options: FormatOption[] = [
    {
      format: 'csv',
      label: 'CSV',
      description: `${filteredCount} customer — buka di Excel / Google Sheets.`,
      icon: FileText,
      build: async () =>
        new Blob([buildCustomerCsv(customers, filters)], {
          type: 'text/csv;charset=utf-8',
        }),
      mime: 'text/csv',
    },
    {
      format: 'xlsx',
      label: 'XLSX',
      description: `${filteredCount} customer — Excel native, column widths otomatis.`,
      icon: FileSpreadsheet,
      build: async () => buildCustomerXlsx(customers, filters),
      mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    {
      format: 'pdf',
      label: 'PDF',
      description: `${filteredCount} customer — branded PDF report.`,
      icon: FileType2,
      build: async () => buildCustomerPdf(customers, filters, company),
      mime: 'application/pdf',
    },
  ];

  const handleExport = async (opt: FormatOption) => {
    if (busyFormat) return;
    setError(null);
    setStaleBuild(false);
    setBusyFormat(opt.format);
    setIsOpen(false);
    try {
      const blob = await opt.build();
      downloadBlob(blob, `${basename}.${opt.format}`);
    } catch (e) {
      setStaleBuild(isChunkLoadError(e));
      setError((e as Error).message || `Gagal export ${opt.label}`);
    } finally {
      setBusyFormat(null);
    }
  };

  return (
    <div className="relative">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        leftIcon={
          busyFormat ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <FileText size={14} />
          )
        }
        rightIcon={<ChevronDown size={12} />}
        loading={!!busyFormat}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {busyFormat
          ? `Export ${busyFormat.toUpperCase()}…`
          : 'Export'}
      </Button>

      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setIsOpen(false)}
            aria-label="Tutup menu"
          />
          <div
            role="menu"
            className="absolute right-0 mt-2 w-72 z-40 bg-surface border border-line rounded-lg shadow-hover py-1.5 overflow-hidden"
          >
            <div className="px-3 pt-1.5 pb-1.5 text-overline uppercase text-ink-subtle font-medium tracking-wider border-b border-line">
              Format
            </div>
            {options.map((opt) => (
              <button
                key={opt.format}
                type="button"
                role="menuitem"
                onClick={() => handleExport(opt)}
                disabled={!!busyFormat}
                className={cn(
                  'w-full px-3 py-2.5 flex items-start gap-3 text-left hover:bg-canvas/70 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                <span className="w-7 h-7 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                  <opt.icon size={14} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="text-small font-medium text-ink">{opt.label}</span>
                    {busyFormat === opt.format && (
                      <Loader2 size={12} className="animate-spin text-primary" />
                    )}
                  </span>
                  <span className="block text-caption text-ink-subtle mt-0.5">
                    {opt.description}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {error && (
        <div className="absolute right-0 mt-2 w-72 z-40 p-2.5 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
          {error}
          {staleBuild && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-2 inline-flex items-center gap-1.5 rounded border border-danger/40 px-2.5 py-1.5 font-medium hover:bg-danger/10"
            >
              <RefreshCw size={12} />
              Muat ulang halaman
            </button>
          )}
        </div>
      )}
    </div>
  );
}
